import { useState, useRef, useEffect } from 'react';

export default function GuessInput({ onGuess, guessesLeft, answered, forfeited, disabled }) {
  const [value, setValue] = useState('');
  const [shake, setShake] = useState(false);
  const [flash, setFlash] = useState(null); // 'correct' | 'wrong'
  const inputRef = useRef(null);

  useEffect(() => {
    if (!answered && !forfeited && !disabled) {
      inputRef.current?.focus();
    }
  }, [answered, forfeited, disabled]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || answered || forfeited || disabled || guessesLeft <= 0) return;
    onGuess(trimmed);
    setValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  // External method to show feedback
  useEffect(() => {
    if (flash) {
      const t = setTimeout(() => setFlash(null), 600);
      return () => clearTimeout(t);
    }
  }, [flash]);

  const isDisabled = answered || forfeited || disabled || guessesLeft <= 0;

  const borderColor = flash === 'correct'
    ? '#22c55e' : flash === 'wrong'
    ? '#ef4444' : 'rgba(99,102,241,0.4)';

  return (
    <div style={styles.wrapper}>
      {/* Guess dots */}
      <div style={styles.dotsRow}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.dot,
              background: i < guessesLeft
                ? '#6366f1'
                : 'rgba(255,255,255,0.1)',
              boxShadow: i < guessesLeft
                ? '0 0 8px rgba(99,102,241,0.5)' : 'none',
            }}
          />
        ))}
        <span style={styles.guessLabel}>
          {guessesLeft} guess{guessesLeft !== 1 ? 'es' : ''} left
        </span>
      </div>

      {/* Input */}
      <div
        style={{
          ...styles.inputRow,
          border: `1.5px solid ${borderColor}`,
          animation: shake ? 'shake 0.3s ease' : 'none',
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        <input
          ref={inputRef}
          style={styles.input}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            answered ? '✅ You answered!' :
            forfeited ? '❌ Out of guesses' :
            'Type your guess...'
          }
          disabled={isDisabled}
          maxLength={80}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <button
          style={{
            ...styles.submitBtn,
            background: isDisabled
              ? 'rgba(99,102,241,0.2)'
              : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
          onClick={handleSubmit}
          disabled={isDisabled}
        >
          →
        </button>
      </div>

      {/* Speed bonus hint */}
      <p style={styles.hint}>
        ⚡ Answer within 10s of a new hint for +50 bonus points
      </p>

      <style>{`
        @keyframes shake {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-6px)}
          40%{transform:translateX(6px)}
          60%{transform:translateX(-4px)}
          80%{transform:translateX(4px)}
        }
      `}</style>
    </div>
  );
}

const styles = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: '10px' },
  dotsRow: {
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  dot: {
    width: '10px', height: '10px', borderRadius: '50%',
    transition: 'all 0.3s ease',
  },
  guessLabel: {
    fontSize: '12px', color: 'rgba(255,255,255,0.35)',
    marginLeft: '4px',
  },
  inputRow: {
    display: 'flex', alignItems: 'center',
    borderRadius: '14px', overflow: 'hidden',
    background: 'rgba(255,255,255,0.05)',
    transition: 'border-color 0.3s ease',
  },
  input: {
    flex: 1, padding: '14px 16px',
    background: 'transparent', border: 'none', outline: 'none',
    color: '#fff', fontSize: '15px',
    fontFamily: "'Syne', sans-serif",
  },
  submitBtn: {
    padding: '14px 20px', border: 'none',
    color: '#fff', fontSize: '20px', fontWeight: '700',
    transition: 'all 0.2s ease',
  },
  hint: {
    margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.25)',
    textAlign: 'center',
  },
};